import * as routeHelpers from "@/router/routeHelpers";
import { CircleAlertIcon, PlusIcon } from "lucide-react";
import React from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { AvailableIntegrationFragment } from "@/apollo/types";
import { IconButton, PrimaryButton } from "@/components/elements/Button";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/elements/Modal";
import Tooltip from "@/components/elements/Tooltip";
import { DestinationIcon } from "@/components/icons/domain-icons";
import cn from "@/helpers/classNames";
import { useDisclosure } from "@/hooks/useDisclosure";
import {
  DestinationsIntegrationsProvider,
  IntegrationLogoBox,
  useIntegration,
} from "@/integrations";
import { AccountType, useUserAccounts } from "@/providers/UserProvider";
import { useCurrentAccountIdValue } from "@/providers/account";
import { LinkToWorkspace } from "@/router";
import { Popover, Transition } from "@headlessui/react";

export function WorkspaceSwitcher({
  children,
  allowCreateDestination,
}: {
  children:
    React.ReactElement | ((currentAccount?: AccountType) => React.ReactElement);
  allowCreateDestination?: boolean;
}) {
  const accounts = useUserAccounts();
  const currentAccountId = useCurrentAccountIdValue();
  const createDestinationDisclosure = useDisclosure();

  const currentAccount = React.useMemo(
    () => accounts.find((x) => x.id === currentAccountId),
    [accounts, currentAccountId],
  );

  let [referenceElement, setReferenceElement] =
    React.useState<HTMLDivElement | null>(null);
  let [popperElement, setPopperElement] = React.useState<HTMLDivElement | null>(
    null,
  );
  let { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "auto-start",
    modifiers: [
      {
        name: "preventOverflow",
        enabled: true,
      },
      { name: "offset", options: { offset: [0, 12] } },
    ],
  });

  const sortedAccounts = React.useMemo(() => {
    return accounts.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts]);

  if (!sortedAccounts.length) {
    return null;
  }
  return (
    <DestinationsIntegrationsProvider>
      <Popover>
        <Popover.Button as="div">
          {React.cloneElement(
            typeof children === "function"
              ? children(currentAccount)
              : React.Children.only(children),
            {
              ref: setReferenceElement,
              role: "button",
            },
          )}
        </Popover.Button>
        {createPortal(
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Popover.Panel
              ref={(elm) => setPopperElement(elm)}
              style={styles.popper}
              {...attributes.popper}
              className="z-50 max-h-screen w-64 overflow-clip rounded-sm border bg-popover shadow-lg"
            >
              <div className="flex h-full flex-col">
                <div className="flex min-h-10 items-center gap-2 border-b bg-foreground/3 py-1 pr-2 pl-4 text-sm font-medium">
                  <DestinationIcon className="size-5" /> Destinations
                  {allowCreateDestination && (
                    <Tooltip content="Create a new destination">
                      <IconButton
                        className="ml-auto"
                        onClick={() => createDestinationDisclosure.onOpen()}
                        icon={<PlusIcon className="size-4" />}
                        aria-label="Create new destination"
                        size="sm"
                        variant="ghost"
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="max-h-96 grow overflow-auto text-sm">
                  {sortedAccounts.map((account) => (
                    <AccountOption
                      account={account}
                      isSelected={account.id === currentAccountId}
                      key={account.id}
                    />
                  ))}
                </div>
              </div>
            </Popover.Panel>
          </Transition>,
          document.body,
        )}
      </Popover>
      <CreateDestinationDialog {...createDestinationDisclosure} />
    </DestinationsIntegrationsProvider>
  );
}

const AccountOption = ({
  account,
  isSelected,
}: {
  account: AccountType;
  isSelected: boolean;
}) => {
  const dataWarehouseIntegration = useIntegration(
    account.dataWarehouse?.integrationId,
  );

  const renderSubTitle = (
    account: AccountType,
    dwIntegration: AvailableIntegrationFragment | undefined | null,
  ) => {
    if (!account.dataWarehouse) {
      return <em>Data warehouse not connected</em>;
    }
    let subTitle = account.dataWarehouse.title;
    if (dwIntegration?.id === "weld-bigquery") {
      subTitle = dwIntegration.name;
    }
    if (account.dataWarehouse.subTitle) {
      subTitle += ` (${account.dataWarehouse.subTitle})`;
    }
    return <span>{subTitle}</span>;
  };

  return (
    <LinkToWorkspace
      slug={account.slug}
      className={cn(
        "group block w-full text-left text-black hover:bg-blue-500 hover:text-white dark:text-white dark:hover:bg-blue-500",
        {
          "bg-muted": isSelected,
        },
      )}
    >
      <div className="flex items-center justify-between space-x-1 py-2 pr-2 pl-4">
        <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate">{account.name}</div>
            <div className="text-xs leading-tight text-muted-foreground group-hover:text-white">
              {renderSubTitle(account, dataWarehouseIntegration)}
            </div>
          </div>
          {!dataWarehouseIntegration ? (
            <Tooltip content="Data warehouse not connected">
              <div className="aspect-square shrink-0 overflow-hidden rounded-full bg-background p-1 dark:bg-gray-900">
                <CircleAlertIcon className="size-5 text-muted-foreground" />
              </div>
            </Tooltip>
          ) : (
            <Tooltip content={dataWarehouseIntegration.name}>
              <div className="aspect-square shrink-0 overflow-hidden rounded-full bg-background p-1 dark:bg-gray-900">
                <IntegrationLogoBox id={dataWarehouseIntegration.id} />
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </LinkToWorkspace>
  );
};

function CreateDestinationDialog(props: ReturnType<typeof useDisclosure>) {
  return (
    <Modal {...props}>
      <ModalCloseButton />
      <ModalHeader className="flex items-center gap-3">
        <DestinationIcon className="size-6" />
        <span>Create new destination</span>
      </ModalHeader>
      <ModalBody>
        <p className="mb-3">
          To proceed with creating a new destination, you'll need to set up a
          new account.
        </p>
        <p>
          Click on the "Create Account" button below and follow the instructions
          to create a new account with a destination.
        </p>
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={() => window.open(routeHelpers.createWorkspaceRoute())}
        >
          Create account
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
}
